const EXCLUDED =
  'script, style, noscript, template, svg, input, select, textarea, [hidden], [inert], [aria-hidden="true"], [contenteditable="true"], [data-a11y-speech-ignore]';

export function isReadableElement(
  element: Element,
  cache = new WeakMap<Element, boolean>(),
  includeControls = false,
): boolean {
  if (element.closest(EXCLUDED) || (!includeControls && element.closest('button'))) return false;

  const cached = cache.get(element);

  if (cached !== undefined) return cached;
  const style = element.ownerDocument.defaultView?.getComputedStyle(element);
  const readable = Boolean(
    style &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.visibility !== 'collapse' &&
      (!element.parentElement || isReadableElement(element.parentElement, cache, includeControls)),
  );

  cache.set(element, readable);

  return readable;
}

export function readVisibleText(root: Element, exclude = '', includeControls = false): string {
  const parts: string[] = [];
  const visible = new WeakMap<Element, boolean>();
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;

      return element && isReadableElement(element, visible, includeControls) && (!exclude || !element.closest(exclude))
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  while (walker.nextNode()) {
    const node = walker.currentNode;

    if (node.nodeType === Node.TEXT_NODE) parts.push(node.textContent ?? '');
    else if ((node as Element).matches('img[alt]')) parts.push((node as Element).getAttribute('alt') ?? '');
    else if ((node as Element).matches('app-redactor[readonly] iframe')) {
      try {
        const body = (node as HTMLIFrameElement).contentDocument?.body;

        if (body) parts.push(readVisibleText(body, exclude));
      } catch {
        // Cross-origin and browser-managed frames cannot expose their text.
      }
    }
  }

  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

export function readSelectedText(owner: Document, root: Element): string {
  const selection = owner.getSelection();

  if (!selection || selection.isCollapsed || !selection.rangeCount) return '';
  const range = selection.getRangeAt(0);

  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return '';

  const parts: string[] = [];
  const visible = new WeakMap<Element, boolean>();
  const walker = owner.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const node = walker.currentNode;

    if (!node.parentElement || !isReadableElement(node.parentElement, visible) || !range.intersectsNode(node)) continue;
    const start = node === range.startContainer ? range.startOffset : 0;
    const end = node === range.endContainer ? range.endOffset : node.textContent?.length;

    parts.push(node.textContent?.slice(start, end) ?? '');
  }

  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

/** Names of links and buttons follow their labels, including icon-only controls. */
export function readControlName(element: Element): string {
  const labelledBy = element.getAttribute('aria-labelledby')?.trim().split(/\s+/);
  const referencedText = labelledBy?.map((id) => element.ownerDocument.getElementById(id)?.textContent ?? '').join(' ');

  return (
    referencedText?.trim() ||
    element.getAttribute('aria-label')?.trim() ||
    readVisibleText(element, '', true) ||
    element.getAttribute('title')?.trim() ||
    ''
  )
    .replace(/\s+/g, ' ')
    .trim();
}
